import { Navigate, createBrowserRouter } from 'react-router-dom';

import { PublicLayout } from '@/app/public/PublicLayout';
import { HomePage } from '@/app/public/HomePage';
import { FeaturesPage } from '@/app/public/FeaturesPage';
import { CommandsPage } from '@/app/public/CommandsPage';
import { DocsPage } from '@/app/public/DocsPage';
import { StatusPage } from '@/app/public/StatusPage';
import { ChangelogPage } from '@/app/public/ChangelogPage';
import { TeamPage } from '@/app/public/TeamPage';

import { DashboardLayout } from '@/app/dashboard/DashboardLayout';
import { GuildSelectorPage } from '@/app/dashboard/GuildSelectorPage';
import { GuildOverviewPage } from '@/app/dashboard/GuildOverviewPage';
import { SecurityPage } from '@/app/dashboard/sections/SecurityPage';
import { AutomodPage } from '@/app/dashboard/sections/AutomodPage';
import { ModerationPage } from '@/app/dashboard/sections/ModerationPage';
import { CommunityLayout } from '@/app/dashboard/sections/community/CommunityLayout';
import { WelcomePage } from '@/app/dashboard/sections/community/WelcomePage';
import { ReactionRolesPage } from '@/app/dashboard/sections/community/ReactionRolesPage';
import { LevelingPage } from '@/app/dashboard/sections/community/LevelingPage';
import { BoosterPage } from '@/app/dashboard/sections/community/BoosterPage';
import { GiveawaysPage } from '@/app/dashboard/sections/community/GiveawaysPage';
import { StarboardPage } from '@/app/dashboard/sections/community/StarboardPage';
import { SuggestionsPage } from '@/app/dashboard/sections/community/SuggestionsPage';
import { AutomationPage } from '@/app/dashboard/sections/AutomationPage';
import { AiPage } from '@/app/dashboard/sections/AiPage';
import { TicketsPage } from '@/app/dashboard/sections/TicketsPage';
import { AnalyticsPage } from '@/app/dashboard/sections/AnalyticsPage';
import { SettingsPage } from '@/app/dashboard/sections/SettingsPage';
import { LogsPage } from '@/app/dashboard/sections/LogsPage';
import { BackupsPage } from '@/app/dashboard/sections/BackupsPage';

import { OwnerLayout } from '@/app/owner/OwnerLayout';
import { OwnerHomePage } from '@/app/owner/OwnerHomePage';
import { AnnouncementsPage } from '@/app/owner/AnnouncementsPage';
import { AiDiagnosticsPage } from '@/app/owner/AiDiagnosticsPage';

import { NotFoundPage } from '@/app/NotFoundPage';

/**
 * Explicit path → page mapping for the 11 non-Overview dashboard sections.
 * Keys must match app/dashboard/navigation.ts's `path` values exactly — the
 * sidebar/mobile-nav and this route table are the two places that encode
 * the 13-section structure, so they're kept in sync by convention (both
 * ultimately trace back to docs/DASHBOARD_INFORMATION_ARCHITECTURE.md).
 *
 * "community" is nested rather than a single element — its 7 sub-features
 * (Welcome, Reaction Roles, Leveling, Booster, Giveaways, Starboard,
 * Suggestions) each get their own route/URL under CommunityLayout's
 * sub-navigation, per docs/DASHBOARD_INFORMATION_ARCHITECTURE.md's Community
 * grouping.
 */
const dashboardSectionRoutes = [
  { path: 'security', element: <SecurityPage /> },
  { path: 'automod', element: <AutomodPage /> },
  { path: 'moderation', element: <ModerationPage /> },
  {
    path: 'community',
    element: <CommunityLayout />,
    children: [
      { index: true, element: <Navigate to="welcome" replace /> },
      { path: 'welcome', element: <WelcomePage /> },
      { path: 'reaction-roles', element: <ReactionRolesPage /> },
      { path: 'leveling', element: <LevelingPage /> },
      { path: 'booster', element: <BoosterPage /> },
      { path: 'giveaways', element: <GiveawaysPage /> },
      { path: 'starboard', element: <StarboardPage /> },
      { path: 'suggestions', element: <SuggestionsPage /> },
    ],
  },
  { path: 'automation', element: <AutomationPage /> },
  { path: 'ai', element: <AiPage /> },
  { path: 'tickets', element: <TicketsPage /> },
  { path: 'analytics', element: <AnalyticsPage /> },
  { path: 'settings', element: <SettingsPage /> },
  { path: 'logs', element: <LogsPage /> },
  { path: 'backups', element: <BackupsPage /> },
];

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/features', element: <FeaturesPage /> },
      { path: '/commands', element: <CommandsPage /> },
      { path: '/docs', element: <DocsPage /> },
      { path: '/status', element: <StatusPage /> },
      { path: '/changelog', element: <ChangelogPage /> },
      { path: '/team', element: <TeamPage /> },
    ],
  },
  {
    path: '/dashboard',
    children: [
      { index: true, element: <GuildSelectorPage /> },
      {
        path: ':guildId',
        element: <DashboardLayout />,
        children: [{ index: true, element: <GuildOverviewPage /> }, ...dashboardSectionRoutes],
      },
    ],
  },
  {
    path: '/owner',
    element: <OwnerLayout />,
    children: [
      { index: true, element: <OwnerHomePage /> },
      { path: 'announcements', element: <AnnouncementsPage /> },
      { path: 'ai', element: <AiDiagnosticsPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
